<template>
  <div
    class="auth-form-container w-full max-w-[400px] mx-auto"
    :aria-label="STRINGS.aria.labels.signupForm"
  >
    <h2
      class="auth-heading font-display text-2xl font-extrabold text-arena-text-bright tracking-[0.06em] text-center mb-6"
    >
      {{ STRINGS.auth.buttons.createAccount }}
    </h2>

    <!-- Invite-required gate -->
    <div
      v-if="!props.inviteToken"
      class="auth-card flex flex-col gap-4 p-6 rounded-xl border border-arena-border bg-arena-surface/80 backdrop-blur-sm items-center text-center"
    >
      <p class="text-lg font-semibold text-arena-text-bright m-0">
        {{ STRINGS.auth.closedBeta.inviteRequired }}
      </p>
      <p class="text-sm text-arena-text-muted m-0">
        {{ STRINGS.auth.closedBeta.inviteExplanation }}
      </p>
      <button
        class="text-energy-primary hover:text-primary-500 underline underline-offset-2 bg-transparent border-none cursor-pointer font-semibold p-0 text-sm"
        data-testid="invite-required-login-link"
        @click="emit('navigate', 'login')"
      >
        {{ STRINGS.auth.links.logIn }}
      </button>
    </div>

    <template v-else>
      <form
        class="auth-card flex flex-col gap-4 p-6 rounded-xl border border-arena-border bg-arena-surface/80 backdrop-blur-sm"
        @submit.prevent="handleSubmit"
        autocomplete="on"
        novalidate
      >
        <!-- Display Name -->
        <div class="flex flex-col gap-1.5">
          <label for="signup-name" class="text-sm font-semibold text-arena-text-bright">
            {{ STRINGS.auth.labels.displayName }}
          </label>
          <div class="relative">
            <InputText
              id="signup-name"
              ref="nameInput"
              v-model="displayName"
              type="text"
              name="username"
              class="auth-input min-h-12 touch-manipulation w-full"
              :placeholder="STRINGS.auth.placeholders.displayName"
              autocomplete="off"
              :maxlength="UI_CONFIG.VALIDATION.PLAYER_NAME_MAX_LENGTH"
              autocorrect="off"
              spellcheck="false"
              data-testid="signup-name-input"
              :aria-invalid="!!nameError"
              @blur="nameTouched = true"
              @keyup.enter="handleSubmit"
            />
          </div>
          <p
            v-if="nameError"
            class="text-xs text-error-500 m-0"
            role="alert"
            aria-live="polite"
            data-testid="signup-name-error"
          >
            {{ nameError }}
          </p>
          <p v-else class="text-xs text-arena-text-muted m-0">
            {{ STRINGS.auth.hints.displayName }}
          </p>
        </div>

        <!-- Email -->
        <div class="flex flex-col gap-1.5">
          <label for="signup-email" class="text-sm font-semibold text-arena-text-bright">
            {{ STRINGS.auth.labels.email }}
          </label>
          <div class="relative">
            <InputText
              id="signup-email"
              v-model="email"
              type="email"
              name="email"
              class="auth-input min-h-12 touch-manipulation w-full"
              :placeholder="STRINGS.auth.placeholders.email"
              autocomplete="username"
              data-testid="signup-email-input"
              @keyup.enter="handleSubmit"
            />
          </div>
          <span class="text-xs text-arena-text-muted">{{ STRINGS.auth.hints.email }}</span>
        </div>

        <!-- Password -->
        <div class="flex flex-col gap-1.5">
          <label for="signup-password" class="text-sm font-semibold text-arena-text-bright">
            {{ STRINGS.auth.labels.password }}
          </label>
          <div class="relative">
            <InputText
              id="signup-password"
              v-model="password"
              name="new-password"
              :type="showPassword ? 'text' : 'password'"
              class="auth-input min-h-12 touch-manipulation w-full pr-10"
              :placeholder="STRINGS.auth.placeholders.password"
              autocomplete="new-password"
              data-testid="signup-password-input"
              @keyup.enter="handleSubmit"
            />
            <button
              type="button"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-arena-text-muted hover:text-arena-text-bright transition-colors p-1"
              :aria-label="STRINGS.aria.labels.togglePasswordVisibility"
              :aria-pressed="showPassword"
              @click="showPassword = !showPassword"
            >
              <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'" aria-hidden="true"></i>
            </button>
          </div>
          <span class="text-xs text-arena-text-muted">{{ STRINGS.auth.hints.password }}</span>
        </div>

        <!-- Error display -->
        <p
          v-if="displayError"
          ref="errorRef"
          class="text-sm text-error-500 text-center m-0"
          role="alert"
          aria-live="polite"
          tabindex="-1"
        >
          {{ displayError }}
        </p>

        <!-- Legal agreement -->
        <p class="legal-agreement" data-testid="signup-legal-agreement">
          {{ STRINGS.legal.signup.agreement }}
          <button class="legal-agreement-link" @click="emit('navigate', 'terms')" type="button">
            {{ STRINGS.legal.signup.termsLink }}
          </button>
          {{ STRINGS.legal.signup.and }}
          <button class="legal-agreement-link" @click="emit('navigate', 'privacy')" type="button">
            {{ STRINGS.legal.signup.privacyLink }}</button
          >.
        </p>

        <!-- Submit button -->
        <PrimeButton
          :disabled="!canSubmit"
          :aria-label="STRINGS.aria.labels.createAccount"
          data-testid="signup-submit-button"
          class="min-h-12 touch-manipulation mt-2"
          :full-width="true"
          @click="handleSubmit"
        >
          <template v-if="auth.isLoading.value">
            {{ STRINGS.auth.buttons.creatingAccount }}<LoadingDots />
          </template>
          <template v-else>
            {{ STRINGS.auth.buttons.createAccount }}
          </template>
        </PrimeButton>
      </form>

      <!-- Footer links -->
      <div class="auth-footer flex flex-col items-center gap-2 mt-6 text-sm text-arena-text-muted">
        <p class="m-0">
          {{ STRINGS.auth.links.haveAccount }}
          <button
            class="text-energy-primary hover:text-primary-500 underline underline-offset-2 bg-transparent border-none cursor-pointer font-semibold p-0"
            @click="emit('navigate', 'login')"
            data-testid="signup-to-login-link"
          >
            {{ STRINGS.auth.links.logIn }}
          </button>
        </p>
        <button
          class="text-arena-text-muted hover:text-arena-text-bright bg-transparent border-none cursor-pointer text-sm p-0"
          @click="emit('navigate', 'homepage')"
          data-testid="signup-back-link"
        >
          {{ STRINGS.auth.links.backToHome }}
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue';
import InputText from 'primevue/inputtext';
import PrimeButton from './ui/PrimeButton.vue';
import LoadingDots from './ui/LoadingDots.vue';
import { useAuth } from '@/composables/useAuth';
import { isAuthError } from '@/types/auth';
import { AUTH_CONFIG, UI_CONFIG } from '@/config/constants';
import STRINGS from '@/constants/strings';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';

const props = defineProps<{ inviteToken?: string }>();

const emit = defineEmits<{
  success: [name: string];
  navigate: [target: 'login' | 'homepage' | 'terms' | 'privacy'];
}>();

const auth = useAuth();

const displayName = ref('');
const email = ref('');
const password = ref('');
const showPassword = ref(false);
const localError = ref('');
const nameTouched = ref(false);
const nameInput = ref();
const errorRef = ref<HTMLElement>();

const nameError = computed(() => {
  if (!nameTouched.value) return '';
  const trimmed = displayName.value.trim();
  if (!trimmed) return '';
  if (!UI_CONFIG.VALIDATION.DISPLAY_NAME_PATTERN.test(trimmed)) {
    return STRINGS.auth.errors.nameInvalidChars;
  }
  if (trimmed.length < UI_CONFIG.VALIDATION.PLAYER_NAME_MIN_LENGTH) {
    return STRINGS.auth.errors.nameTooShort;
  }
  return '';
});

const displayError = computed(() => {
  if (auth.rateLimitCountdown.value > 0) {
    return STRINGS.templates.rateLimitCountdown(auth.rateLimitCountdown.value);
  }
  if (auth.error.value) {
    return auth.error.value.message;
  }
  return localError.value;
});

const canSubmit = computed(() => {
  const trimmed = displayName.value.trim();
  return (
    trimmed.length >= UI_CONFIG.VALIDATION.PLAYER_NAME_MIN_LENGTH &&
    trimmed.length <= UI_CONFIG.VALIDATION.PLAYER_NAME_MAX_LENGTH &&
    UI_CONFIG.VALIDATION.DISPLAY_NAME_PATTERN.test(trimmed) &&
    password.value.length > 0 &&
    !auth.isLoading.value &&
    auth.rateLimitCountdown.value === 0
  );
});

async function handleSubmit(): Promise<void> {
  if (!props.inviteToken) return;
  if (!canSubmit.value) return;

  localError.value = '';
  auth.clearError();

  const trimmedName = displayName.value.trim();

  if (password.value.length < AUTH_CONFIG.PASSWORD_MIN_LENGTH) {
    localError.value = STRINGS.auth.errors.passwordTooShort;
    focusError();
    return;
  }

  const trimmedEmail = email.value.trim();
  if (!trimmedEmail) {
    localError.value = STRINGS.auth.errors.emailRequired;
    focusError();
    return;
  }
  if (!isValidEmail(trimmedEmail)) {
    localError.value = STRINGS.auth.errors.emailInvalid;
    focusError();
    return;
  }

  try {
    await auth.signup({
      name: trimmedName,
      email: trimmedEmail,
      password: password.value,
      invite_token: props.inviteToken,
    });
    emit('success', trimmedName);
  } catch (err: unknown) {
    if (!isAuthError(err)) {
      reportClientError(err, ErrorLocation.AuthSignupUnexpected);
    }
    focusError();
  }
}

function isValidEmail(value: string): boolean {
  const idx = value.indexOf('@');
  return idx > 0 && idx < value.length - 1;
}

function focusError(): void {
  nextTick(() => {
    errorRef.value?.focus();
  });
}

onMounted(() => {
  nextTick(() => {
    const el = nameInput.value?.$el ?? nameInput.value;
    const input = el?.querySelector?.('input') ?? el;
    if (input && typeof input.focus === 'function') {
      input.focus();
    }
  });
});
</script>

<style scoped>
.auth-heading {
  animation: homepageEntrance 0.8s ease-out both;
}

.auth-card {
  animation: homepageEntrance 0.8s ease-out 0.2s both;
}

.auth-footer {
  animation: homepageEntrance 0.8s ease-out 0.3s both;
}

@keyframes homepageEntrance {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.auth-input {
  padding: 0.75rem 1rem;
  border: 2px solid var(--color-arena-border);
  border-radius: var(--radius-input, 0.5rem);
  font-size: 1rem;
  transition: none;
  outline: none;
  background: rgba(26, 22, 18, 0.8);
  color: var(--color-arena-text-bright);
}

.auth-input:focus {
  outline: none;
}

.auth-input::placeholder {
  color: var(--color-arena-text-muted);
}

.legal-agreement {
  font-size: 0.6875rem;
  line-height: 1.5;
  color: var(--color-arena-text-dim);
  text-align: center;
  margin: 0;
}

.legal-agreement-link {
  color: var(--color-arena-text-muted);
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 150ms ease;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;
  padding: 0;
}

.legal-agreement-link:hover {
  color: var(--color-arena-text-bright);
}

@media (prefers-reduced-motion: reduce) {
  .auth-heading,
  .auth-card,
  .auth-footer {
    animation: none !important;
  }
}
</style>
